import { apiRequest } from './client';
import type { Paginated } from '@loanaudit/shared';

/**
 * Typed API client for the ingestion module.
 *
 * The upload flow is two-phase:
 *   1. createLoanFile → server returns presigned PUT URLs per document
 *   2. For each document: fetch(PUT url, body=file) → then registerUploaded()
 *   3. submit() to move draft → received and kick off processing
 *
 * SHA-256 is computed client-side using Web Crypto.
 */

export interface LoanFileListItem {
  id: string;
  loanNumber: string;
  borrowerName: string;
  productCode: string;
  branchCode: string;
  officerId: string | null;
  source: string;
  state: string;
  rejectReason: string | null;
  documentCount: number;
  receivedAt: string;
  submittedAt: string | null;
  acceptedAt: string | null;
  updatedAt: string;
}

export interface LoanFileDocument {
  id: string;
  originalFileName: string;
  s3Key: string;
  mimeType: string;
  fileSizeBytes: number;
  sha256Hex: string | null;
  uploadState: 'pending' | 'uploaded' | 'failed';
  scanState: 'pending' | 'clean' | 'infected' | 'error';
  scanEngine: string | null;
  documentType: string;
  pageCount: number | null;
  uploadedAt: string | null;
}

export interface LoanFileEvent {
  id: string;
  fromState: string | null;
  toState: string;
  eventType: string;
  actorUserId: string | null;
  actorKind: 'user' | 'system' | 'connector';
  reason: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface LoanFileDetail extends LoanFileListItem {
  failedReason: string | null;
  metadata: Record<string, unknown>;
  documents: LoanFileDocument[];
  events: LoanFileEvent[];
}

export interface CreateLoanFileResponse {
  loanFileId: string;
  state: string;
  documents: Array<{
    documentId: string;
    originalFileName: string;
    s3Key: string;
    uploadUrl: string;
    uploadUrlExpiresAt: string;
  }>;
}

export interface LoanFilesQuery {
  page: number;
  pageSize: number;
  state?: string;
  productCode?: string;
  branchCode?: string;
  search?: string;
  from?: string;
  to?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const ingestionApi = {
  list: (q: LoanFilesQuery) =>
    apiRequest<Paginated<LoanFileListItem>>('/api/ingestion/loan-files', {
      query: q as unknown as Record<string, string | number | boolean | undefined>,
    }),

  get: (id: string) =>
    apiRequest<LoanFileDetail>(`/api/ingestion/loan-files/${id}`),

  /**
   * Get a short-lived presigned URL to preview a parsed document inline.
   * Backend issues a 15-min expiry URL. Used by the evidence drawer.
   */
  getDocumentUrl: (loanFileId: string, documentId: string) =>
    apiRequest<{
      documentId: string;
      loanFileId: string;
      originalFileName: string;
      mimeType: string;
      url: string;
      expiresAt: string;
      pageCount: number | null;
    }>(`/api/ingestion/loan-files/${loanFileId}/documents/${documentId}/url`),

  create: (body: {
    loanNumber: string;
    borrowerName: string;
    productCode: string;
    branchCode: string;
    officerId?: string;
    documents: Array<{ originalFileName: string; mimeType: string; fileSizeBytes: number }>;
    metadata?: Record<string, unknown>;
  }) =>
    apiRequest<CreateLoanFileResponse>('/api/ingestion/loan-files', {
      method: 'POST',
      body,
    }),

  registerUploaded: (loanFileId: string, documentId: string, sha256Hex: string) =>
    apiRequest<{ documentId: string; duplicateOf: string | null }>(
      `/api/ingestion/loan-files/${loanFileId}/documents/registered`,
      {
        method: 'POST',
        body: { documentId, sha256Hex },
      },
    ),

  submit: (id: string, notes?: string) =>
    apiRequest<{ state: string }>(`/api/ingestion/loan-files/${id}/submit`, {
      method: 'POST',
      body: { notes },
    }),

  reject: (id: string, reason: string) =>
    apiRequest<{ rejected: true }>(`/api/ingestion/loan-files/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),

  retrigger: (id: string) =>
    apiRequest<{ retriggered: true }>(`/api/ingestion/loan-files/${id}/retrigger`, {
      method: 'POST',
    }),

  /**
   * Soft-delete a loan file. The row is hidden from listings and returns
   * 404 on subsequent get() calls, but documents in S3, validation
   * findings, and audit events are retained for compliance. Restore is
   * a manual SQL UPDATE (deleted_at = NULL).
   */
  remove: (id: string) =>
    apiRequest<{ id: string; loanNumber: string; deletedAt: string }>(
      `/api/ingestion/loan-files/${id}`,
      { method: 'DELETE' },
    ),

  listConnectors: () =>
    apiRequest<Array<{ key: string; displayName: string }>>('/api/ingestion/connectors'),

  sweepConnector: (key: string) =>
    apiRequest<{
      runId: string;
      connectorKey: string;
      discovered: number;
      succeeded: number;
      failed: number;
      loanFileIds: string[];
      errors: Array<{ ref: string; error: string }>;
    }>(`/api/ingestion/connectors/${encodeURIComponent(key)}/sweep`, { method: 'POST' }),

  listConnectorRuns: (limit = 25) =>
    apiRequest<
      Array<{
        id: string;
        connectorKey: string;
        status: string;
        loanFilesIngested: number;
        loanFilesFailed: number;
        startedAt: string;
        finishedAt: string | null;
        errorMessage: string | null;
      }>
    >('/api/ingestion/connector-runs', { query: { limit } }),
};

/** Compute SHA-256 of a Blob using the browser's Web Crypto API. */
export async function sha256OfBlob(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

/**
 * Direct-to-S3 PUT using the presigned URL. Throws on non-2xx.
 *
 * Uses XMLHttpRequest (not fetch) so we can report upload progress
 * back to the UI as the bytes go up. fetch() has no upload progress
 * events — the streams API was supposed to fix this but lands too
 * late/unevenly across browsers to rely on. XHR.upload.onprogress is
 * boring but works everywhere.
 *
 * The optional onProgress callback fires repeatedly during the upload
 * with a 0-1 value (fraction of bytes sent). It fires at least once
 * on completion at 1.0.
 */
export async function putFileToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded / e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1);
        resolve();
      } else {
        reject(
          new Error(
            `Upload failed (${xhr.status}): ${(xhr.responseText || '').slice(0, 200)}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      // Generic network error — most commonly CORS preflight failure.
      // The browser's CORS error message is in the console, not here.
      reject(
        new Error(
          'Upload network error. If this is a fresh deployment, check that the S3 bucket has a CORS rule allowing your origin.',
        ),
      );
    };

    xhr.onabort = () => reject(new Error('Upload aborted'));

    xhr.send(file);
  });
}
